import { CROSS_LANES, CROSS_STOP_X } from '../../content/junctions';
import { TRAFFIC_KINDS, VARIANTS_PER_KIND } from '../../content/traffic';
import { recoverFromKnock } from '../knock';
import type { Rng } from '../../core/rng';
import type { BusState, CrossCar, RunState } from '../../game/RunState';
import { crossMayGo, junctionAt, nextJunction } from './signals';

const ACTIVE_WITHIN = 260;
const SPAWN_X = 60;
const FOLLOW_GAP = 3;
const LINE_TOLERANCE = 0.1;
const CROSS_KINDS = TRAFFIC_KINDS.filter((k) => !k.hoppable && k.id !== 'truck');

let nextId = 1;

function spawn(state: RunState, seq: number, rng: Rng): void {
  const dir: 1 | -1 = rng.next() < 0.5 ? 1 : -1;
  const lanes = dir === 1 ? CROSS_LANES.eastbound : CROSS_LANES.westbound;
  const kind = rng.weighted(CROSS_KINDS);
  state.crossTraffic.push({
    id: nextId++,
    kind,
    junctionSeq: seq,
    dir,
    laneOffset: rng.pick(lanes),
    x: -dir * SPAWN_X,
    speed: rng.range(9, 12),
    variant: rng.int(0, VARIANTS_PER_KIND - 1),
  });
}

function stopX(car: CrossCar): number {
  return car.dir === 1 ? CROSS_STOP_X.fromLeft : CROSS_STOP_X.fromRight;
}

/** How far this car can go before the stop line or the car ahead of it. */
function room(car: CrossCar, state: RunState, mayGo: boolean): number {
  let limit = Infinity;
  const front = car.x + (car.dir * car.kind.length) / 2;
  // A car resting right on its line is still waiting (not "past it"), or it would creep through on red.
  const beforeLine = car.dir * (stopX(car) - front) > -LINE_TOLERANCE;
  if (!mayGo && beforeLine) limit = car.dir * (stopX(car) - front);
  for (const other of state.crossTraffic) {
    if (other === car || other.junctionSeq !== car.junctionSeq || other.laneOffset !== car.laneOffset) continue;
    const gap = car.dir * (other.x - car.x) - (other.kind.length + car.kind.length) / 2;
    if (gap > -0.5) limit = Math.min(limit, gap - FOLLOW_GAP);
  }
  return limit;
}

/** Cross traffic at the junction ahead: it drives through while we are on red and waits at its line otherwise. */
export function updateCrossTraffic(state: RunState, dt: number, rng: Rng): void {
  const time = state.stats.seconds;
  const ahead = nextJunction(state.bus.s);
  if (ahead.seq !== state.junction.seq) state.junction = { seq: ahead.seq, announced: false, crossedStopLine: false, spawnTimer: 0, zebraSent: 0, zebraTimer: 0, pedestrianFined: false };
  const active = ahead.position - state.bus.s < ACTIVE_WITHIN;
  if (active && crossMayGo(ahead.seq, time)) {
    state.junction.spawnTimer -= dt;
    if (state.junction.spawnTimer <= 0) {
      spawn(state, ahead.seq, rng);
      state.junction.spawnTimer = rng.range(0.7, 1.5);
    }
  }
  for (const car of state.crossTraffic) {
    if (recoverFromKnock(car, dt)) continue;
    const mayGo = crossMayGo(car.junctionSeq, time);
    const step = Math.min(car.speed * dt, Math.max(0, room(car, state, mayGo)));
    car.x += car.dir * step;
  }
  state.crossTraffic = state.crossTraffic.filter(
    (car) => Math.abs(car.x) <= SPAWN_X + 5 && junctionAt(car.junctionSeq).position > state.bus.s - 80,
  );
}

/** A crossing car that the trotro is touching, if any. */
export function crossCarHitting(bus: BusState, cars: readonly CrossCar[]): CrossCar | null {
  for (const car of cars) {
    const carS = junctionAt(car.junctionSeq).position + car.laneOffset;
    const overlapS = Math.abs(bus.s - carS) < (bus.size.length + car.kind.width) / 2;
    const overlapX = Math.abs(bus.x - car.x) < (bus.size.width + car.kind.length) / 2;
    if (overlapS && overlapX && bus.y < car.kind.height) return car;
  }
  return null;
}
